import { useEffect, useState } from "react";
import type { SectionSidebarNavLinkItem } from "@/components/SectionSidebarNav";
import { canViewHref } from "@/lib/acl";
import { useCurrentUser } from "@/lib/auth";

export interface AuswertungenNavItem extends SectionSidebarNavLinkItem {
  /** Abweichende ACL-Seite; ohne Angabe gilt die Bereichs-ACL /auswertungen.html. */
  aclHref?: string;
}

export const AUSWERTUNGEN_NAV_ITEMS: AuswertungenNavItem[] = [
  {
    key: "hero-usage",
    label: "Hero-Zeiten",
    href: "/auswertungen/hero-zeiten",
  },
  {
    key: "open-hours-ranking",
    label: "Offene Stunden",
    href: "/auswertungen/offene-stunden",
  },
  {
    key: "hours-pipeline",
    label: "Stunden-Pipeline",
    href: "/auswertungen/stunden-pipeline",
  },
  {
    key: "outstanding-balances",
    label: "Außenstände",
    href: "/auswertungen/aussenstaende",
  },
  {
    key: "project-completions",
    label: "Projektabschlüsse",
    href: "/auswertungen/projektabschluesse",
  },
  {
    key: "festpreis-hours",
    label: "Festpreis-Stunden",
    href: "/auswertungen/festpreis-stunden",
  },
  {
    key: "invoice-revenue",
    label: "Umsatz & Zahlungen",
    href: "/auswertungen/umsatz",
    aclHref: "/auswertungen/umsatz.html",
  },
  {
    key: "open-invoices",
    label: "Offene Posten",
    href: "/auswertungen/offene-posten",
  },
  {
    key: "offer-statistics",
    label: "Angebote",
    href: "/auswertungen/angebote",
  },
  {
    key: "document-margins",
    label: "Marge & Aufteilung",
    href: "/auswertungen/marge",
  },
  {
    key: "portal-usage",
    label: "Portal-Nutzung",
    href: "/auswertungen/portal-nutzung",
  },
];

/** Nav-Einträge gefiltert nach ACL: Items mit eigenem aclHref nur bei Berechtigung. */
export function useVisibleAuswertungenNavItems(): SectionSidebarNavLinkItem[] {
  const { user } = useCurrentUser();
  const groups = (user?.groups as string[] | undefined) ?? [];
  const groupsKey = groups.join("|");
  const [items, setItems] = useState<SectionSidebarNavLinkItem[]>(() =>
    AUSWERTUNGEN_NAV_ITEMS.filter((item) => !item.aclHref),
  );

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void (async () => {
      const checks = await Promise.all(
        AUSWERTUNGEN_NAV_ITEMS.map((item) =>
          item.aclHref ? canViewHref(item.aclHref, groups) : Promise.resolve(true),
        ),
      );
      if (!cancelled) {
        setItems(AUSWERTUNGEN_NAV_ITEMS.filter((_, index) => checks[index]));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, groupsKey]);

  return items;
}

export const AUSWERTUNGEN_SECTION_SUBTITLES: Record<string, string> = {
  "hero-usage":
    "Kalenderübersicht der Hero-Zeiten nach Monat.",
  "open-hours-ranking":
    "Jahres-Rankings für offene Projekt- und Mitarbeiterstunden ohne Stundennachweis.",
  "hours-pipeline":
    "Aufschlüsselung aller Stunden von der Zeiterfassung ohne Nachweis bis zur Bezahlung.",
  "outstanding-balances":
    "Ca.-Bewertung noch nicht abgerechneter Stunden, Materialien, Fahrtkilometer und Achtel.",
  "project-completions":
    "Abgeschlossene Projekte aller Auftragsarten mit Durchlaufzeit von Erstellung bis Bezahlt.",
  "festpreis-hours":
    "Ist- vs. Kalkulationsstunden aller Festpreis-Projekte eines Jahres mit Ampel-Bewertung.",
  "invoice-revenue":
    "Rechnungsumsatz Hero plus App, Vorkasse aus dem Vorjahr für den validen Umsatz und Prognose zum Jahresende.",
  "open-invoices":
    "Offene Ausgangsrechnungen (App + Hero) mit Restsaldo, Fälligkeit und Altersstruktur je Kunde.",
  "offer-statistics":
    "Angebotsvolumen pro Monat und Umwandlungsquote von Angeboten zu Aufträgen/Rechnungen.",
  "document-margins":
    "Lohn/Material-Aufteilung und Marge aus den synchronisierten Hero-Dokumentdetails.",
  "portal-usage":
    "Anmeldeversuche und Nutzung des Kundenportals: aktive Kunden, seltene Nutzer und unbekannte Zugangsversuche.",
};
